import {AnyAction} from '@reduxjs/toolkit';
import {QueryClient, useQueryClient} from '@tanstack/react-query';
import i18n, {changeLanguage} from 'i18next';
import {Dispatch, ReactElement} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {NavigateFunction} from 'react-router';
import {useNavigate} from 'react-router-dom';

import {requestLogout} from 'App/features/user/userSaga';
import {selectUserCredentials} from 'App/features/user/userSlice';
import {User} from 'App/features/user/userTypes';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import MenuButton from 'Components/menu_button';
import ProfileAvatar from 'Components/profile_avatar';
import font from 'Style/font';
import getConfig from '~/load_config';

import color from '../../../../color';

const styles = {
  profileAvatar: {
    marginRight: '4px',
  },
  menuButton: {
    width: '30px',
  },
  contact: {
    flexDirection: 'column',
    padding: '10px 18px',
    fontSize: font.size.body,
    color: color.grayL,
  },
};

type TOption = {
  value: string;
  label: string;
  onClick: () => void;
};

function getUserMenuOptions(
  isOrgAdmin: boolean,
  dispatch: Dispatch<AnyAction>,
  navigate: NavigateFunction,
  queryClient: QueryClient,
): TOption[] {
  const orgAdminOption = {
    value: 'admin',
    label: 'Administration',
    onClick: () => navigate('/admin'),
  };

  const currentLanguage = i18n.language;

  return [
    {
      value: 'account',
      label: 'Profile',
      onClick: () => navigate('/account/profile'),
    },
    ...(isOrgAdmin ? [orgAdminOption] : []),
    {
      value: 'release_notes',
      label: 'Novelties',
      onClick: () => navigate('/release_notes'),
    },
    {
      value: 'language',
      label: 'Change language',
      onClick: () => changeLanguage(currentLanguage === 'fr' ? 'en' : 'fr'),
    },
    {
      value: 'logout',
      label: 'Logout',
      onClick: () => {
        const config = getConfig();

        dispatch(requestLogout());

        if (config.isProduction) {
          window.location.replace('http://buildrz.io/');
        } else {
          queryClient.removeQueries();
        }
      },
    },
  ];
}

function Avatar({profile}: {profile: User}) {
  return (
    <ProfileAvatar
      profile={profile}
      width={25}
      height={25}
      extraStyle={styles.profileAvatar}
    />
  );
}

export default function AuthenticatedMenu(): ReactElement | null {
  const {user} = useSelector(selectUserCredentials) || {};

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  if (!user) {
    return null;
  }

  const userMenuOptions = getUserMenuOptions(
    user.is_org_admin,
    dispatch,
    navigate,
    queryClient,
  );

  const handleOptionClick = (optionValue: string) =>
    userMenuOptions.find(({value}) => value === optionValue)?.onClick?.();

  return (
    <MenuButton
      triggerElement={<Avatar profile={user} />}
      options={userMenuOptions}
      onOptionClick={handleOptionClick}
      side="left"
      highlightOnHover={false}
      hasSeenAllReleaseNotes={user.has_seen_all_release_notes}
      menuType="profile"
      extraStyle={styles.menuButton}
    />
  );
}
